// Plane Geometry (Planimetry) Calculators helpers: area, perimeter, and other
// key measurements for the twelve most commonly searched 2D shapes/topics -
// triangle (Heron's formula), Pythagorean theorem, circle, circle sector,
// rectangle, square, parallelogram, trapezoid, rhombus, regular polygon,
// ellipse, and kite.

const PI = Math.PI

// ============================================================
// 1. Triangle Area Calculator (Heron's formula, three sides)
// ============================================================
export function triangleAreaCalculator(params: { side_a: unknown; side_b: unknown; side_c: unknown; language: unknown }): {
    area: number; perimeter: number; steps: string
} {
    const a = Math.max(Number(params.side_a) || 0, 0)
    const b = Math.max(Number(params.side_b) || 0, 0)
    const c = Math.max(Number(params.side_c) || 0, 0)

    const perimeter = a + b + c
    const s = perimeter / 2
    const areaSq = s * (s - a) * (s - b) * (s - c)
    const area = areaSq > 0 ? Math.sqrt(areaSq) : 0

    const steps = [
        `s = (a + b + c) / 2 = (${a} + ${b} + ${c}) / 2 = ${s.toFixed(4)}`,
        `Area = √(s(s-a)(s-b)(s-c)) = √(${s.toFixed(4)} × ${(s - a).toFixed(4)} × ${(s - b).toFixed(4)} × ${(s - c).toFixed(4)}) = ${area.toFixed(4)}`,
        `Perimeter = a + b + c = ${a} + ${b} + ${c} = ${perimeter.toFixed(4)}`,
    ].join('\n')

    return { area, perimeter, steps }
}

// ============================================================
// 2. Pythagorean Theorem Calculator (solve for a, b, or c)
// ============================================================
export function pythagoreanTheoremCalculator(params: { solve_for: unknown; leg_a: unknown; leg_b: unknown; hypotenuse_c: unknown; language: unknown }): {
    result: number; steps: string
} {
    const solveFor = String(params.solve_for || 'hypotenuse_c')
    const a = Math.max(Number(params.leg_a) || 0, 0)
    const b = Math.max(Number(params.leg_b) || 0, 0)
    const c = Math.max(Number(params.hypotenuse_c) || 0, 0)

    if (solveFor === 'leg_a') {
        const inner = c * c - b * b
        const result = inner > 0 ? Math.sqrt(inner) : 0
        return { result, steps: `a = √(c² - b²) = √(${c}² - ${b}²) = ${result.toFixed(4)}` }
    }
    if (solveFor === 'leg_b') {
        const inner = c * c - a * a
        const result = inner > 0 ? Math.sqrt(inner) : 0
        return { result, steps: `b = √(c² - a²) = √(${c}² - ${a}²) = ${result.toFixed(4)}` }
    }
    const result = Math.sqrt(a * a + b * b)
    return { result, steps: `c = √(a² + b²) = √(${a}² + ${b}²) = ${result.toFixed(4)}` }
}

// ============================================================
// 3. Circle Area & Circumference Calculator
// ============================================================
export function circleCalculator(params: { radius: unknown; language: unknown }): {
    area: number; circumference: number; diameter: number; steps: string
} {
    const r = Math.max(Number(params.radius) || 0, 0)

    const area = PI * r * r
    const circumference = 2 * PI * r
    const diameter = 2 * r

    const steps = [
        `Area = πr² = π × ${r}² = ${area.toFixed(4)}`,
        `Circumference = 2πr = 2 × π × ${r} = ${circumference.toFixed(4)}`,
        `Diameter = 2r = 2 × ${r} = ${diameter.toFixed(4)}`,
    ].join('\n')

    return { area, circumference, diameter, steps }
}

// ============================================================
// 4. Circle Sector Area & Arc Length Calculator
// ============================================================
export function circleSectorCalculator(params: { radius: unknown; angle_deg: unknown; language: unknown }): {
    sector_area: number; arc_length: number; steps: string
} {
    const r = Math.max(Number(params.radius) || 0, 0)
    const angle = Math.min(Math.max(Number(params.angle_deg) || 0, 0), 360)
    const fraction = angle / 360

    const sectorArea = fraction * PI * r * r
    const arcLength = fraction * 2 * PI * r

    const steps = [
        `Fraction of circle = angle / 360 = ${angle} / 360 = ${fraction.toFixed(4)}`,
        `Sector Area = (angle/360) × πr² = ${fraction.toFixed(4)} × π × ${r}² = ${sectorArea.toFixed(4)}`,
        `Arc Length = (angle/360) × 2πr = ${fraction.toFixed(4)} × 2 × π × ${r} = ${arcLength.toFixed(4)}`,
    ].join('\n')

    return { sector_area: sectorArea, arc_length: arcLength, steps }
}

// ============================================================
// 5. Rectangle Area & Perimeter Calculator
// ============================================================
export function rectangleCalculator(params: { length: unknown; width: unknown; language: unknown }): {
    area: number; perimeter: number; diagonal: number; steps: string
} {
    const l = Math.max(Number(params.length) || 0, 0)
    const w = Math.max(Number(params.width) || 0, 0)

    const area = l * w
    const perimeter = 2 * (l + w)
    const diagonal = Math.sqrt(l * l + w * w)

    const steps = [
        `Area = l × w = ${l} × ${w} = ${area.toFixed(4)}`,
        `Perimeter = 2(l + w) = 2 × (${l} + ${w}) = ${perimeter.toFixed(4)}`,
        `Diagonal = √(l² + w²) = √(${l}² + ${w}²) = ${diagonal.toFixed(4)}`,
    ].join('\n')

    return { area, perimeter, diagonal, steps }
}

// ============================================================
// 6. Square Area & Perimeter Calculator
// ============================================================
export function squareCalculator(params: { side_a: unknown; language: unknown }): {
    area: number; perimeter: number; diagonal: number; steps: string
} {
    const a = Math.max(Number(params.side_a) || 0, 0)

    const area = a * a
    const perimeter = 4 * a
    const diagonal = a * Math.sqrt(2)

    const steps = [
        `Area = a² = ${a}² = ${area.toFixed(4)}`,
        `Perimeter = 4a = 4 × ${a} = ${perimeter.toFixed(4)}`,
        `Diagonal = a√2 = ${a} × √2 = ${diagonal.toFixed(4)}`,
    ].join('\n')

    return { area, perimeter, diagonal, steps }
}

// ============================================================
// 7. Parallelogram Area & Perimeter Calculator
// ============================================================
export function parallelogramCalculator(params: { base: unknown; height: unknown; side_b: unknown; language: unknown }): {
    area: number; perimeter: number; steps: string
} {
    const base = Math.max(Number(params.base) || 0, 0)
    const height = Math.max(Number(params.height) || 0, 0)
    const sideB = Math.max(Number(params.side_b) || 0, 0)

    const area = base * height
    const perimeter = 2 * (base + sideB)

    const steps = [
        `Area = base × height = ${base} × ${height} = ${area.toFixed(4)}`,
        `Perimeter = 2(base + side) = 2 × (${base} + ${sideB}) = ${perimeter.toFixed(4)}`,
    ].join('\n')

    return { area, perimeter, steps }
}

// ============================================================
// 8. Trapezoid Area & Perimeter Calculator
// ============================================================
export function trapezoidCalculator(params: { base_a: unknown; base_b: unknown; height: unknown; leg_c: unknown; leg_d: unknown; language: unknown }): {
    area: number; perimeter: number; steps: string
} {
    const a = Math.max(Number(params.base_a) || 0, 0)
    const b = Math.max(Number(params.base_b) || 0, 0)
    const h = Math.max(Number(params.height) || 0, 0)
    const c = Math.max(Number(params.leg_c) || 0, 0)
    const d = Math.max(Number(params.leg_d) || 0, 0)

    const area = 0.5 * (a + b) * h
    const perimeter = a + b + c + d

    const steps = [
        `Area = ½(a + b)h = ½ × (${a} + ${b}) × ${h} = ${area.toFixed(4)}`,
        `Perimeter = a + b + c + d = ${a} + ${b} + ${c} + ${d} = ${perimeter.toFixed(4)}`,
    ].join('\n')

    return { area, perimeter, steps }
}

// ============================================================
// 9. Rhombus Area & Perimeter Calculator (from the two diagonals)
// ============================================================
export function rhombusCalculator(params: { diagonal_p: unknown; diagonal_q: unknown; language: unknown }): {
    area: number; side: number; perimeter: number; steps: string
} {
    const p = Math.max(Number(params.diagonal_p) || 0, 0)
    const q = Math.max(Number(params.diagonal_q) || 0, 0)

    const area = (p * q) / 2
    const side = Math.sqrt((p / 2) * (p / 2) + (q / 2) * (q / 2))
    const perimeter = 4 * side

    const steps = [
        `Area = (p × q) / 2 = (${p} × ${q}) / 2 = ${area.toFixed(4)}`,
        `Side = √((p/2)² + (q/2)²) = √((${p}/2)² + (${q}/2)²) = ${side.toFixed(4)}`,
        `Perimeter = 4 × side = 4 × ${side.toFixed(4)} = ${perimeter.toFixed(4)}`,
    ].join('\n')

    return { area, side, perimeter, steps }
}

// ============================================================
// 10. Regular Polygon Area & Perimeter Calculator
// ============================================================
export function regularPolygonCalculator(params: { num_sides: unknown; side_length: unknown; language: unknown }): {
    area: number; perimeter: number; apothem: number; steps: string
} {
    const n = Math.min(Math.max(Math.round(Number(params.num_sides) || 0), 3), 100)
    const s = Math.max(Number(params.side_length) || 0, 0)

    const perimeter = n * s
    const apothem = s / (2 * Math.tan(PI / n))
    const area = (perimeter * apothem) / 2

    const steps = [
        `Perimeter = n × s = ${n} × ${s} = ${perimeter.toFixed(4)}`,
        `Apothem = s / (2 × tan(π/n)) = ${s} / (2 × tan(π/${n})) = ${apothem.toFixed(4)}`,
        `Area = (Perimeter × Apothem) / 2 = (${perimeter.toFixed(4)} × ${apothem.toFixed(4)}) / 2 = ${area.toFixed(4)}`,
    ].join('\n')

    return { area, perimeter, apothem, steps }
}

// ============================================================
// 11. Ellipse Area & Perimeter Calculator (perimeter via Ramanujan's 2nd approximation)
// ============================================================
export function ellipseCalculator(params: { semi_axis_a: unknown; semi_axis_b: unknown; language: unknown }): {
    area: number; perimeter: number; steps: string
} {
    const a = Math.max(Number(params.semi_axis_a) || 0, 0)
    const b = Math.max(Number(params.semi_axis_b) || 0, 0)

    const area = PI * a * b
    const h = a + b > 0 ? Math.pow((a - b), 2) / Math.pow((a + b), 2) : 0
    const perimeter = PI * (a + b) * (1 + (3 * h) / (10 + Math.sqrt(4 - 3 * h)))

    const steps = [
        `Area = πab = π × ${a} × ${b} = ${area.toFixed(4)}`,
        `h = (a-b)²/(a+b)² = (${a}-${b})²/(${a}+${b})² = ${h.toFixed(4)}`,
        `Perimeter ≈ π(a+b)(1 + 3h/(10+√(4-3h))) (Ramanujan approximation) ≈ ${perimeter.toFixed(4)}`,
    ].join('\n')

    return { area, perimeter, steps }
}

// ============================================================
// 12. Kite Area & Perimeter Calculator
// ============================================================
export function kiteCalculator(params: { diagonal_p: unknown; diagonal_q: unknown; side_a: unknown; side_b: unknown; language: unknown }): {
    area: number; perimeter: number; steps: string
} {
    const p = Math.max(Number(params.diagonal_p) || 0, 0)
    const q = Math.max(Number(params.diagonal_q) || 0, 0)
    const a = Math.max(Number(params.side_a) || 0, 0)
    const b = Math.max(Number(params.side_b) || 0, 0)

    const area = (p * q) / 2
    const perimeter = 2 * (a + b)

    const steps = [
        `Area = (p × q) / 2 = (${p} × ${q}) / 2 = ${area.toFixed(4)}`,
        `Perimeter = 2(a + b) = 2 × (${a} + ${b}) = ${perimeter.toFixed(4)}`,
    ].join('\n')

    return { area, perimeter, steps }
}
